
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { motion, useScroll, useSpring, useTransform } from 'framer-motion';
import {
  ArrowLeft,
  Calendar,
  Clock,
  Share2,
  Heart,
  Linkedin,
  Twitter,
  Facebook,
  Link as LinkIcon,
  ChevronRight,
  Globe,
  MessageCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:4100";

const BlogDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [blogPost, setBlogPost] = useState(null);
  const [relatedBlogs, setRelatedBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [isLiked, setIsLiked] = useState(false);
  const [likes, setLikes] = useState(0);
  const [showShareMenu, setShowShareMenu] = useState(false);

  // Scroll Progress Bar
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  // Parallax Header
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 500], [0, 250]);
  const opacity = useTransform(scrollY, [0, 300], [1, 0]);

  useEffect(() => {
    if (slug) {
      const likedHash = localStorage.getItem(`blog-liked-${slug}`);
      setIsLiked(likedHash === 'true');
      fetchBlogPost();
      fetchRelatedBlogs();
      window.scrollTo(0, 0);
    }
  }, [slug]);

  const fetchBlogPost = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/public/blog/${slug}`);
      if (res.data.success) {
        setBlogPost(res.data.data);
        setLikes(res.data.data.likes || 0);
      }
    } catch (error) {
      console.error("Error loading blog:", error);
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedBlogs = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/public/blog/${slug}/related?limit=3`);
      if (res.data.success) setRelatedBlogs(res.data.data);
    } catch (error) {
      console.error("Error loading related:", error);
    }
  };

  const handleLike = async () => {
    if (!blogPost) return;
    const newIsLiked = !isLiked;
    setIsLiked(newIsLiked);
    setLikes(newIsLiked ? likes + 1 : Math.max(0, likes - 1));

    if (newIsLiked) localStorage.setItem(`blog-liked-${slug}`, 'true');
    else localStorage.removeItem(`blog-liked-${slug}`);

    try {
      await axios.put(`${API_URL}/admin/public/blog/${blogPost._id}/like`, {
        action: newIsLiked ? 'like' : 'unlike'
      });
    } catch (error) {
      setIsLiked(!newIsLiked);
      setLikes(isLiked ? likes : likes + 1);
    }
  };

  const handleShare = (platform) => {
    const url = window.location.href;
    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
    };
    if (platform === 'copy') {
      navigator.clipboard.writeText(url);
      setShowShareMenu(false);
    } else {
      window.open(shareUrls[platform], '_blank');
      setShowShareMenu(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
        <p className="text-indigo-900 font-semibold animate-pulse">Loading Article...</p>
      </div>
    </div>
  );

  if (!blogPost) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="text-center max-w-md">
        <h2 className="text-3xl font-bold text-slate-800 mb-4">Article Not Found</h2>
        <p className="text-slate-500 mb-2">We couldn't seem to find the article you're looking for.</p>
        <p className="text-xs text-slate-400 font-mono mb-2 bg-slate-100 p-2 rounded">Debug ID: {slug}</p>
        <p className="text-xs text-slate-400 font-mono mb-8 bg-slate-100 p-2 rounded">
          API: {API_URL}<br />
          Error: {error?.message || "Unknown"}
        </p>
        <button
          onClick={() => navigate('/blog')}
          className="px-6 py-3 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 transition-colors flex items-center gap-2 mx-auto"
        >
          <ArrowLeft className="w-5 h-5" /> Back to Blog
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F8FAFC] font-sans text-slate-900">

      {/* Reading Progress */}
      <motion.div className="fixed top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 origin-left z-50 shadow-lg shadow-indigo-500/20" style={{ scaleX }} />

      {/* Floating Navbar */}
      <nav className="fixed top-6 left-1/2 -translate-x-1/2 w-[90%] md:max-w-4xl bg-white/80 backdrop-blur-md rounded-full shadow-2xl border border-white/50 z-40 px-6 py-3 flex items-center justify-between transition-all">
        <button onClick={() => navigate('/blog')} className="flex items-center gap-2 text-sm font-bold text-slate-600 hover:text-indigo-600 transition-colors">
          <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center group-hover:bg-indigo-100 transition-colors">
            <ArrowLeft className="w-4 h-4" />
          </div>
          <span className="hidden sm:inline">Back to Blog</span>
        </button>

        <div className="flex items-center gap-3">
          {/* Like */}
          <button onClick={handleLike} className={`group flex items-center gap-2 px-4 py-2 rounded-full transition-all duration-300 ${isLiked ? 'bg-rose-50 text-rose-600 ring-2 ring-rose-200' : 'bg-slate-50 text-slate-600 hover:bg-rose-50 hover:text-rose-600'}`}>
            <Heart className={`w-4 h-4 transition-transform group-hover:scale-110 ${isLiked ? 'fill-rose-500' : ''}`} />
            <span className="font-bold text-sm">{likes}</span>
          </button>

          {/* Share */}
          <div className="relative">
            <button onClick={() => setShowShareMenu(!showShareMenu)} className="w-10 h-10 rounded-full bg-indigo-50 text-indigo-600 flex items-center justify-center hover:bg-indigo-600 hover:text-white transition-all shadow-lg hover:shadow-indigo-600/30">
              <Share2 className="w-4 h-4" />
            </button>
            {showShareMenu && (
              <div className="absolute right-0 mt-3 w-48 bg-white border border-slate-100 shadow-2xl rounded-2xl p-2 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                <button onClick={() => handleShare('twitter')} className="w-full px-4 py-2.5 text-left text-sm font-medium hover:bg-slate-50 rounded-xl flex gap-3 items-center text-slate-600 hover:text-indigo-600"><Twitter className="w-4 h-4" /> Twitter</button>
                <button onClick={() => handleShare('linkedin')} className="w-full px-4 py-2.5 text-left text-sm font-medium hover:bg-slate-50 rounded-xl flex gap-3 items-center text-slate-600 hover:text-indigo-600"><Linkedin className="w-4 h-4" /> LinkedIn</button>
                <button onClick={() => handleShare('facebook')} className="w-full px-4 py-2.5 text-left text-sm font-medium hover:bg-slate-50 rounded-xl flex gap-3 items-center text-slate-600 hover:text-indigo-600"><Facebook className="w-4 h-4" /> Facebook</button>
                <button onClick={() => handleShare('copy')} className="w-full px-4 py-2.5 text-left text-sm font-medium hover:bg-slate-50 rounded-xl flex gap-3 items-center text-slate-600 hover:text-indigo-600"><LinkIcon className="w-4 h-4" /> Copy Link</button>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* IMMERSIVE HERO */}
      <div className="relative h-[60vh] lg:h-[70vh] w-full overflow-hidden bg-slate-900">
        <motion.div style={{ y, opacity }} className="absolute inset-0 w-full h-full">
          {blogPost.image ? (
            <div
              className="w-full h-full bg-cover bg-center"
              style={{ backgroundImage: `url(${blogPost.image.startsWith('http') ? blogPost.image : `${API_URL}/uploads/${blogPost.image}`})` }}
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-indigo-900 to-purple-900" />
          )}
          {/* Overlay Gradient */}
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/60 to-transparent"></div>
        </motion.div>

        <div className="absolute bottom-0 left-0 right-0 p-6 pb-24 md:pb-32 flex justify-center">
          <div className="max-w-4xl w-full text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white text-xs font-bold uppercase tracking-widest mb-6 shadow-xl"
            >
              <span className="w-2 h-2 rounded-full bg-indigo-500 animate-pulse"></span>
              {blogPost.category}
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-4xl md:text-6xl font-extrabold text-white leading-tight mb-8 drop-shadow-lg"
            >
              {blogPost.title}
            </motion.h1>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="flex items-center justify-center gap-6 text-slate-300 text-sm font-medium"
            >
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-indigo-400" />
                {new Date(blogPost.createdAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </div>
              <div className="w-1.5 h-1.5 rounded-full bg-slate-600"></div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-pink-400" />
                {Math.ceil((blogPost.content?.length || 0) / 1000)} min read
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* CONTENT CARD */}
      <main className="relative z-10 -mt-20 px-4 pb-24">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="max-w-4xl mx-auto bg-white rounded-3xl shadow-2xl shadow-indigo-900/10 p-8 md:p-14 border border-slate-100"
        >
          {/* Author Header */}
          <div className="flex items-center justify-between border-b border-slate-100 pb-8 mb-10">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-gradient-to-tr from-indigo-500 to-purple-500 p-[3px]">
                <div className="w-full h-full rounded-full bg-white flex items-center justify-center text-xl font-black text-indigo-600">
                  {blogPost.author?.substring(0, 2).toUpperCase() || 'AU'}
                </div>
              </div>
              <div>
                <div className="font-bold text-lg text-slate-800">{blogPost.author}</div>
                <div className="text-indigo-500 text-sm font-medium">Content Creator</div>
              </div>
            </div>

            <div className="hidden sm:flex gap-2">
              {blogPost.tags?.map(tag => (
                <span key={tag} className="px-3 py-1 bg-slate-100 text-slate-600 rounded-lg text-xs font-bold uppercase tracking-wide hover:bg-slate-200 transition-colors">
                  #{tag}
                </span>
              ))}
            </div>
          </div>

          {/* Markdown Content */}
          <div className="prose prose-lg prose-slate max-w-none 
                prose-headings:font-bold prose-headings:text-slate-900 
                prose-p:text-slate-600 prose-p:leading-8 prose-p:mb-6
                prose-a:text-indigo-600 prose-a:no-underline hover:prose-a:underline
                prose-blockquote:border-l-4 prose-blockquote:border-indigo-500 prose-blockquote:bg-indigo-50 prose-blockquote:px-6 prose-blockquote:py-4 prose-blockquote:rounded-r-lg prose-blockquote:italic prose-blockquote:text-indigo-900
                prose-img:rounded-2xl prose-img:shadow-xl
                prose-strong:text-indigo-700
            ">
            {blogPost.content?.split('\n\n').map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>

          {/* Bottom Interaction */}
          <div className="mt-16 pt-10 border-t border-slate-100 flex flex-col md:flex-row items-center justify-between gap-6 bg-slate-50/50 rounded-2xl p-8">
            <div className="text-center md:text-left">
              <h3 className="font-bold text-slate-900 text-lg mb-1">Enjoyed this article?</h3>
              <p className="text-slate-500 text-sm">Share it with your network or leave a like.</p>
            </div>
            <div className="flex gap-4">
              <button onClick={handleLike} className={`px-6 py-3 rounded-xl font-bold transition-all shadow-lg hover:shadow-xl flex items-center gap-2 ${isLiked ? 'bg-gradient-to-r from-rose-500 to-pink-500 text-white shadow-rose-500/30' : 'bg-white text-slate-700 hover:bg-slate-50'}`}>
                <Heart className={`w-5 h-5 ${isLiked ? 'fill-white' : ''}`} />
                {isLiked ? 'Liked' : 'Like'}
              </button>
              <button onClick={() => setShowShareMenu(!showShareMenu)} className="px-6 py-3 rounded-xl font-bold bg-indigo-600 text-white shadow-lg shadow-indigo-500/30 hover:bg-indigo-700 transition-all flex items-center gap-2">
                <Share2 className="w-5 h-5" /> Share
              </button>
            </div>
          </div>

        </motion.div>

        {/* Related Posts */}
        {relatedBlogs.length > 0 && (
          <div className="max-w-6xl mx-auto mt-24">
            <div className="flex items-center gap-4 mb-10 pl-4">
              <div className="p-3 rounded-xl bg-indigo-100 text-indigo-600">
                <Sparkles className="w-6 h-6" />
              </div>
              <h3 className="text-3xl font-extrabold text-slate-900">Read Next</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {relatedBlogs.map(blog => (
                <Link
                  key={blog._id}
                  to={`/blog/${blog.slug || blog._id}`}
                  className="group block h-full"
                >
                  <motion.div
                    whileHover={{ y: -10 }}
                    className="bg-white rounded-2xl overflow-hidden shadow-xl shadow-slate-200/50 border border-slate-100 flex flex-col h-full"
                  >
                    <div className="h-48 overflow-hidden relative">
                      <div className="absolute inset-0 bg-indigo-900/10 group-hover:bg-transparent transition-colors z-10"></div>
                      {blog.image && (
                        <img
                          src={blog.image.startsWith('http') ? blog.image : `${API_URL}/uploads/${blog.image}`}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                          alt=""
                        />
                      )}
                      <div className="absolute bottom-3 left-3 z-20">
                        <span className="px-2 py-1 bg-white/90 backdrop-blur text-[10px] font-bold uppercase rounded text-indigo-800 shadow-sm">{blog.category}</span>
                      </div>
                    </div>
                    <div className="p-6 flex-1 flex flex-col">
                      <h4 className="font-bold text-lg text-slate-900 mb-2 leading-tight group-hover:text-indigo-600 transition-colors">{blog.title}</h4>
                      <p className="text-sm text-slate-500 line-clamp-2 mb-4 flex-1">{blog.description}</p>
                      <div className="flex items-center gap-2 text-indigo-600 text-sm font-bold mt-auto">
                        Read Now <ArrowLeft className="w-4 h-4 rotate-180 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </motion.div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </main>

      {/* Decorative Footer Gradient */}
      <div className="h-64 bg-gradient-to-t from-slate-100 to-transparent pointer-events-none -mt-32 relative z-0"></div>
    </div>
  );
};

const Sparkles = ({ className }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 0 0 1-1.275-1.275L12 3Z" /><path d="M5 3v4" /><path d="M9 3v4" /><path d="M3 5h4" /><path d="M3 9h4" /></svg>
);

export default BlogDetailPage;